// OVO-113 — Discover Schedules modal.
//
// Scans the partner's recent orders (last 90 days by default) and
// proposes (pickupSite, deliverySite, weekday-mask) candidates. The
// operator reviews each row — can tweak weekdays, swap sides, edit
// time windows — then bulk-saves the validated subset.
//
// Backed by GET /partners/:partnerId/schedules/discover.

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Input, message, Modal, Segmented, Select, Space, Table, Tag, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { discoverSchedules, createSchedule } from '../modules/order-schedules/api';
import { listSitesForPartner } from '../modules/sites/api';

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function WeekdayBar({ value = [false, false, false, false, false, false, false], onChange, disabled }) {
  const toggle = (i) => {
    if (disabled) return;
    const next = value.slice();
    next[i] = !next[i];
    onChange?.(next);
  };
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {DAY_LABELS.map((label, i) => (
        <button
          key={i}
          type="button"
          onClick={() => toggle(i)}
          disabled={disabled}
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            border: value[i] ? '1px solid var(--accent-strong)' : '1px solid #d9d9d9',
            background: value[i] ? 'var(--accent-strong)' : '#fff',
            color: value[i] ? '#fff' : '#bbb',
            fontSize: 11,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function DiscoverSchedulesModal({ partnerId, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  // OVO-166 — operator-selectable look-back window (days). Default 180 so
  // bi-weekly / monthly patterns are seen; widen to 365 for the long tail.
  const [windowDays, setWindowDays] = useState(180);

  // Pull candidates + partner Sites in parallel when the modal opens.
  // Sites populate the inline pickers so the operator can re-route a
  // candidate to a different Site before saving (e.g. when Discover
  // proposed a Site that's about to be archived).
  useEffect(() => {
    if (!open || !partnerId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    Promise.all([discoverSchedules(partnerId, windowDays), listSitesForPartner(partnerId)])
      .then(([{ response, error }, sitesRes]) => {
        if (cancelled) return;
        if (error) {
          message.error(error.message || 'Discover failed');
          return;
        }
        setData(response);
        setRows(
          (response?.candidates || []).map((c, i) => ({
            ...c,
            key: i,
            // Each row carries its own editable copy.
            editPickupSiteId: c.pickupSite?.id ?? null,
            editDeliverySiteId: c.deliverySite?.id ?? null,
            editWeekdays: c.weekdays,
            editTimeStart: c.deliveryTimeStart || '',
            editTimeEnd: c.deliveryTimeEnd || '',
            selected: true,
          })),
        );
        if (Array.isArray(sitesRes.response)) {
          setSiteOptions(
            sitesRes.response
              .filter((s) => !s.isArchived)
              .map((s) => ({ value: s.id, label: `${s.name} — ${s.address}` })),
          );
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, partnerId, windowDays]);

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const selected = useMemo(() => rows.filter((r) => r.selected), [rows]);
  const allChecked = rows.length > 0 && rows.every((r) => r.selected);
  const someChecked = rows.some((r) => r.selected);

  const handleSave = async () => {
    if (selected.length === 0) {
      message.warning('Select at least one candidate');
      return;
    }
    // Pre-validate: each selected row needs (a) at least one weekday
    // checked, and (b) at least one Site set.
    const invalid = selected.find(
      (r) => !r.editWeekdays.some(Boolean) || (!r.editPickupSiteId && !r.editDeliverySiteId),
    );
    if (invalid) {
      message.error('Each selected row must have at least one weekday AND at least one Site');
      return;
    }
    setSaving(true);
    try {
      // Sequential createSchedule calls — same pattern as Sites bulk-
      // create. Tens of rows max; per-row failures don't block others.
      let created = 0;
      let failed = 0;
      const startDate = dayjs().format('YYYY-MM-DD');
      for (const r of selected) {
        const dto = {
          pickupSiteId: r.editPickupSiteId ?? undefined,
          deliverySiteId: r.editDeliverySiteId ?? undefined,
          weekdays: r.editWeekdays,
          startDate,
          deliveryTimeStart: r.editTimeStart || undefined,
          deliveryTimeEnd: r.editTimeEnd || undefined,
          // OVO-148b — carry the inferred recurring products onto the
          // schedule so materialized orders ship WITH products (OVO-147).
          // Strip the display-only fields; null weight = driver captures.
          defaultProducts:
            r.defaultProducts?.length > 0
              ? r.defaultProducts.map((p) => ({
                  partnerItemTypeId: p.partnerItemTypeId,
                  quantity: p.quantity,
                  weight: p.weight ?? undefined,
                }))
              : undefined,
        };
        const { error } = await createSchedule(partnerId, dto);
        if (error) failed++;
        else created++;
      }
      if (created > 0) {
        message.success(
          `Created ${created} schedule${created === 1 ? '' : 's'}${failed > 0 ? ` — ${failed} failed` : ''}`,
        );
      } else if (failed > 0) {
        message.error(`All ${failed} attempts failed`);
      }
      onSaved?.();
    } catch (e) {
      message.error(e?.message || 'Bulk save failed');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={allChecked}
          indeterminate={!allChecked && someChecked}
          onChange={(e) => setRows((prev) => prev.map((r) => ({ ...r, selected: e.target.checked })))}
        />
      ),
      key: 'select',
      width: 40,
      render: (_, r) => (
        <Checkbox checked={r.selected} onChange={(e) => updateRow(r.key, { selected: e.target.checked })} />
      ),
    },
    {
      title: 'Pickup site',
      key: 'pickup',
      width: 220,
      render: (_, r) => (
        <Select
          allowClear
          showSearch
          size="small"
          optionFilterProp="label"
          value={r.editPickupSiteId}
          placeholder="(tour-mapped)"
          options={siteOptions}
          onChange={(v) => updateRow(r.key, { editPickupSiteId: v ?? null })}
          disabled={!r.selected}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Delivery site',
      key: 'delivery',
      width: 220,
      render: (_, r) => (
        <Select
          allowClear
          showSearch
          size="small"
          optionFilterProp="label"
          value={r.editDeliverySiteId}
          placeholder="(tour-mapped)"
          options={siteOptions}
          onChange={(v) => updateRow(r.key, { editDeliverySiteId: v ?? null })}
          disabled={!r.selected}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Days',
      key: 'weekdays',
      width: 200,
      render: (_, r) => (
        <Tooltip
          title={
            <div style={{ fontSize: 11 }}>
              Occurrences by weekday:
              <br />
              {DAY_LABELS.map((d, i) => (
                <span key={i} style={{ marginRight: 6 }}>
                  {d}:{r.occurrencesPerDay?.[i] || 0}
                </span>
              ))}
            </div>
          }
        >
          <span>
            <WeekdayBar
              value={r.editWeekdays}
              onChange={(v) => updateRow(r.key, { editWeekdays: v })}
              disabled={!r.selected}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Window',
      key: 'window',
      width: 170,
      render: (_, r) => (
        <Space size={4}>
          <Input
            size="small"
            value={r.editTimeStart}
            placeholder="09:00"
            onChange={(e) => updateRow(r.key, { editTimeStart: e.target.value })}
            disabled={!r.selected}
            style={{ width: 70 }}
          />
          <span style={{ color: '#bbb' }}>–</span>
          <Input
            size="small"
            value={r.editTimeEnd}
            placeholder="12:00"
            onChange={(e) => updateRow(r.key, { editTimeEnd: e.target.value })}
            disabled={!r.selected}
            style={{ width: 70 }}
          />
        </Space>
      ),
    },
    {
      // OVO-148b — the recurring products inferred from the pattern's
      // orders (mode product set, ≥50% of the group). Saved onto the
      // schedule as defaultProducts; "driver weighs" = null weight on a
      // requires-weight type (pickup-weight enrichment).
      title: 'Products',
      key: 'products',
      width: 150,
      render: (_, r) => {
        const products = r.defaultProducts || [];
        if (products.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <Tooltip title={`Seen on ${products[0]?.occurrences ?? '?'} of ${r.totalOrders} orders`}>
            <span>
              {products.map((p, i) => (
                <Tag key={i} color="purple" style={{ fontSize: 11, marginBottom: 2, marginInlineEnd: 2 }}>
                  {p.quantity}× {p.name}
                  {p.weight != null
                    ? ` · ${p.weight}${p.unitOfMeasure || 'kg'}`
                    : p.requiresWeight
                      ? ' · driver weighs'
                      : ''}
                </Tag>
              ))}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Confidence',
      key: 'confidence',
      width: 140,
      render: (_, r) => {
        const orders = r.totalOrders || 0;
        const weeks = r.weeksWithOccurrence || 0;
        const high = orders >= 8 && weeks >= 6;
        return (
          <Tooltip title={r.sampleOrderRefs?.length ? `Samples: ${r.sampleOrderRefs.join(', ')}` : ''}>
            <span style={{ fontSize: 12 }}>
              <Tag color={high ? 'green' : 'gold'} style={{ marginInlineEnd: 4 }}>
                {high ? 'high' : 'medium'}
              </Tag>
              <span style={{ color: 'var(--text-muted)' }}>
                {orders} order{orders === 1 ? '' : 's'} / {weeks}w
              </span>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Discover Schedules — propose recurring patterns from historical orders"
      width={1200}
      onOk={handleSave}
      okText={`Add ${selected.length} schedule${selected.length === 1 ? '' : 's'}`}
      okButtonProps={{ disabled: selected.length === 0 || saving, loading: saving }}
      destroyOnClose
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Look back:</span>
        <Segmented
          size="small"
          value={windowDays}
          onChange={setWindowDays}
          disabled={loading}
          options={[
            { label: '90 days', value: 90 },
            { label: '180 days', value: 180 },
            { label: '1 year', value: 365 },
          ]}
        />
        <span style={{ color: '#aaa', fontSize: 12 }}>Widen to catch bi-weekly / monthly visits.</span>
      </div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          data
            ? `Scanned ${data.totalOrdersScanned} orders over ${data.windowDays}d (${
                data.effectiveWeeks ?? data.weeksInWindow
              } weeks of data) · ${data.existingScheduleCount} existing schedules ignored · ${rows.length} new candidates`
            : 'Scanning the partner’s recent orders…'
        }
        description="A weekday is proposed when the (pickup, delivery) pair occurred at least 3 times on that day AND in at least 50% of the weeks the data covers. Edit any row’s sites, weekdays, or time window before saving. Existing schedules are auto-filtered."
      />
      {data && rows.length === 0 && (
        <Alert
          type="warning"
          icon={<WarningOutlined />}
          message="No new recurring patterns detected"
          description={
            data.totalOrdersScanned === 0 && data.ordersWithoutSite > 0
              ? `${data.ordersWithoutSite} orders in the window aren’t linked to any Site, so Discover can’t see them. Create the partner’s Sites first (Sites tab → Discover Sites) — historical orders are linked automatically — then run Discover again.`
              : 'Either the partner already has schedules for every pattern in the data, or there aren’t enough repeating orders yet to meet the confidence threshold.'
          }
          showIcon
        />
      )}
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="key"
        size="small"
        loading={loading}
        pagination={rows.length > 50 ? { pageSize: 50 } : false}
        // Without a fixed layout the long Site labels make the auto-layout
        // table ignore the declared column widths and overflow the modal
        // (cells drawn past the right edge, over the page behind). Fixed
        // layout enforces the widths; the Selects ellipsize internally.
        tableLayout="fixed"
        scroll={{ x: 990 }}
      />
    </Modal>
  );
}
